/**
 * P3-16: VillageTab —— Village tab 组装根。
 *
 * 结构对齐 js/village.js:4874-5152：
 * - Jobs 面板：职业按钮列表 + Clear Jobs + Loadouts 区（Delete all /
 *   Show|Remove default / Create）+ 自由小猫数；
 * - Map 面板：feature flag + archery 控制（本阶段保留旧路径占位，VILLAGE_MAP
 *   默认关闭，P3-17 切换前评估是否接入）；
 * - Management 面板：happiness/festival duration 统计 + Hunt/Festival/
 *   Optimize/Promote/Redeem Gift 按钮；
 * - Census 面板：P3-15 已迁移的 Census 组件。
 *
 * 显隐规则：
 * - Jobs：!(ironWill && !getKittens())
 * - Management：getKittens() >= 5 || zebras > 0
 * - Census：civil 已研究
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { ClearJobsBtn } from "./ClearJobsBtn";
import { JobButton } from "./JobButton";
import { LoadoutButton } from "./LoadoutButton";
import { Census } from "./Census";
import { FestivalBtn } from "./FestivalBtn";
import { HuntBtn } from "./HuntBtn";
import {
  OptimizeJobsBtn,
  PromoteKittensBtn,
  RedeemGiftBtn,
} from "./ManagementButtons";

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

interface Loadout {
  title: string;
  isDefault: boolean;
  [k: string]: unknown;
}

interface LoadoutController {
  loadouts: Loadout[];
  toggleDefaultLoadouts(): void;
}

interface VillageState {
  jobsHidden: boolean;
  managementHidden: boolean;
  censusHidden: boolean;
  freeKittens: string;
  happinessHtml: string;
  loadouts: Loadout[];
  defaultOn: boolean;
  loadoutsUnlocked: boolean;
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

function toVillageState(game: GamePage | null): VillageState | null {
  if (!game) {
    return null;
  }
  const village = game.village;
  const loadoutController = village.loadoutController as LoadoutController;
  const loadouts = loadoutController?.loadouts ?? [];
  const defaultOn = loadouts.some((lo) => lo.isDefault);

  const happiness = village.happiness * 100;
  const happinessVal =
    happiness < 10000 ? String(Math.floor(happiness)) : game.getDisplayValueExt(happiness);
  let happinessHtml = `${t("village.census.lbl.happiness")}: ${happinessVal}%`;
  const festivalDays = game.calendar.festivalDays;
  if (festivalDays) {
    happinessHtml += `<br><br> ${t("village.census.lbl.festival.duration")} ${game.toDisplayDays(festivalDays)}`;
  }

  return {
    jobsHidden: game.ironWill && !village.getKittens(),
    managementHidden: village.getKittens() < 5 && game.resPool.get("zebras").value === 0,
    censusHidden: !game.science.get("civil").researched,
    freeKittens: `${t("village.general.free.kittens.label")}: ${village.getFreeKittens()} / ${village.getDiligentKittens()}`,
    happinessHtml,
    loadouts,
    defaultOn,
    loadoutsUnlocked: game.prestige.getPerk("engeneering").researched,
  };
}

function villageStateEq(a: VillageState | null, b: VillageState | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.jobsHidden === b.jobsHidden &&
    a.managementHidden === b.managementHidden &&
    a.censusHidden === b.censusHidden &&
    a.freeKittens === b.freeKittens &&
    a.happinessHtml === b.happinessHtml &&
    a.loadoutsUnlocked === b.loadoutsUnlocked &&
    a.defaultOn === b.defaultOn &&
    a.loadouts.length === b.loadouts.length &&
    a.loadouts.every((lo, i) => lo.title === b.loadouts[i].title && lo.isDefault === b.loadouts[i].isDefault)
  );
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export default function VillageTab() {
  const state = useGameTick(toVillageState, villageStateEq);

  const onDeleteAll = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const game = getGame();
    if (!game) {
      return;
    }
    game.ui.confirm("", t("village.loadout.delete.all.confirm"), () => {
      game.village.loadoutController.loadouts = [];
      game.render();
    });
  }, []);

  const onToggleDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    getGame()?.village.loadoutController.toggleDefaultLoadouts();
  }, []);

  const onCreate = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const game = getGame();
    if (!game) {
      return;
    }
    const com = (window as unknown as Record<string, unknown>).com as Record<string, unknown> | undefined;
    const nuclearunicorn = com?.nuclearunicorn as Record<string, unknown> | undefined;
    const gameVillage = nuclearunicorn?.village as Record<string, unknown> | undefined;
    const Loadout = gameVillage?.Loadout as new (game: GamePage) => Loadout | undefined;
    if (!Loadout) {
      return;
    }
    game.village.loadoutController.loadouts.push(new Loadout(game));
    game.render();
  }, []);

  const jobNames = useMemo(() => {
    const game = getGame();
    return game?.village.jobs.map((job: { name: string }) => job.name) ?? [];
  }, []);

  if (!state) {
    return null;
  }

  return (
    <div className="tabInner Village">
      <div style={{ display: state.jobsHidden ? "none" : "block" }}>
        {jobNames.map((name: string) => (
          <JobButton key={name} jobName={name} />
        ))}
        <ClearJobsBtn />

        <div
          className="loadout"
          style={{
            float: "right",
            width: "50%",
            visibility: state.loadoutsUnlocked ? "visible" : "hidden",
          }}
        >
          {state.loadouts.map((loadout, i) => (
            <LoadoutButton key={loadout.title + i} loadout={loadout} />
          ))}
          <a
            href="#"
            className="loadoutHref"
            style={{ float: "right" }}
            title="Delete all loadouts"
            onClick={onDeleteAll}
          >
            {t("village.loadout.delete.all")}
          </a>
          <a
            href="#"
            className="loadoutHref"
            title="Default loadouts"
            onClick={onToggleDefault}
          >
            {state.defaultOn ? t("village.loadout.remove.default") : t("village.loadout.show.default")}
          </a>
          <div style={{ clear: "both" }} />
        </div>

        <table className="table" style={{ width: "100%" }}>
          <tr>
            <td colSpan={2} style={{ width: "50%" }}>{state.freeKittens}</td>
          </tr>
          <tr>
            <td>{t("village.loadout.title")}</td>
            <td style={{ visibility: state.loadoutsUnlocked ? "visible" : "hidden" }}>
              <a
                href="#"
                className="loadoutHref"
                style={{ float: "right" }}
                title="Create Loadout"
                onClick={onCreate}
              >
                {t("village.loadout.create")}
              </a>
            </td>
          </tr>
        </table>
      </div>

      <div style={{ display: state.managementHidden ? "none" : "block" }}>
        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td
                style={{
                  cursor: "pointer",
                  width: "50%",
                  textAlign: "center",
                }}
                title={t("village.panel.management.desc")}
                dangerouslySetInnerHTML={{ __html: state.happinessHtml }}
              />
              <td>
                <HuntBtn />
                <FestivalBtn />
                <OptimizeJobsBtn />
                <PromoteKittensBtn />
                <RedeemGiftBtn />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: state.censusHidden ? "none" : "block" }}>
        <Census />
      </div>
    </div>
  );
}
